import { useState } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
/**
 * CartPage - Full-screen cart page for mobile
 * Shows cart items, totals, and checkout button
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Trash2, Plus, Minus, Pencil } from 'lucide-react';
import WeightSelectionModal from '../../components/ecommerce/products/WeightSelectionModal';
import type { CartItem } from '../../types/ecommerce.types';
import { useEcommerceCart, cleArticle } from '../../hooks/ecommerce/useEcommerceCart';
import { useCustomer } from '../../contexts/CustomerContext';
import { useTenant } from '../../contexts/TenantContext';
import { formatQty } from '../../utils/pos.utils';
import { useShopOrdering } from '../../hooks/ecommerce/useShopOrdering';
import { formatMomentBelge, isOrderableNow, formatJourReouverture } from '../../utils/shopOrdering';
import VidangesAnnonce from '../../components/ecommerce/VidangesAnnonce';
import BacsAnnonce from '../../components/ecommerce/BacsAnnonce';

export default function CartPage() {
  useTitrePage('Mon panier');
  const ordering = useShopOrdering();
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, updateSelection, getTotals, clearCart, getItemCount, vidanges, setVidanges, bacs, setBacs } = useEcommerceCart();
  // Ligne au poids / à la pièce en cours de modification (rouvre le choix du poids)
  const [editing, setEditing] = useState<CartItem | null>(null);
  // Hors fenêtre, l'épicerie et les pains sur commande passent quand même :
  // seuls les articles réellement concernés bloquent le passage en caisse.
  const articlesBloques = items.filter(item => !isOrderableNow(item.product, ordering));
  const { isAuthenticated } = useCustomer();
  const { tenant } = useTenant();
  const primaryColor = tenant?.config?.ecommerce?.primaryColor || '#10b981';

  const totals = getTotals();
  const itemCount = getItemCount();

  const formatPrice = (cents: number) => (cents / 100).toFixed(2) + ' \u20ac';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center gap-2 text-gray-700"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Boutique</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900">
            Mon panier ({itemCount})
          </h1>
          {items.length > 0 && (
            <button
              onClick={() => {
                if (confirm('Vider le panier ?')) clearCart();
              }}
              className="text-red-500 text-sm font-medium"
            >
              Vider
            </button>
          )}
        </div>
      </header>

      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <ShoppingCart className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Votre panier est vide</h2>
          <p className="text-gray-600 mb-6">
            Parcourez notre boutique et ajoutez des produits
          </p>
          <button
            onClick={() => navigate('/shop')}
            className="px-6 py-3 rounded-xl text-white font-semibold"
            style={{ backgroundColor: primaryColor }}
          >
            Voir les produits
          </button>
        </div>
      ) : (
        <>
          {/* Items list */}
          <div className="flex-1 px-4 py-3 space-y-3">
            {items.map((item) => {
              const isLocked = item.addedBy === 'vendor';

              return (
                <div key={cleArticle(item)} className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                  <div className="flex gap-3">
                    {/* Product image */}
                    <div className="w-16 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {item.product.image_url ? (
                        <img
                          src={item.product.image_url}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl text-gray-300">
                          <ShoppingCart className="w-6 h-6" />
                        </div>
                      )}
                    </div>

                    {/* Product info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm truncate">
                        {item.product.name}
                      </h3>
                      {item.prep_options?.includes('coupé') && (
                        <span className="block text-xs font-semibold text-[#4a5c3a]">🔪 Coupé</span>
                      )}
                      {item.lot && (
                        <span className="block text-xs font-semibold text-red-700">
                          🏷️ Anti-gaspi −{item.lot.percent} %{item.lot.expiry_date ? ` · date ${String(item.lot.expiry_date).slice(0, 10).split('-').reverse().slice(0, 2).join('/')}` : ''}
                        </span>
                      )}
                      {/* Ce que le client a réellement mis au panier. Sans cette
                          ligne, 500 g d'amarante s'affichaient « 1 » : la
                          quantité est un NOMBRE DE PORTIONS, le poids vit dans
                          weight_grams. Le total, lui, était juste — d'où un
                          panier qui semblait se contredire. Le panneau de la
                          version bureau l'affiche depuis toujours. */}
                      {item.piece_count ? (
                        /* Commandé à la pièce : le nombre d'abord, le poids
                           estimé (base du prix) entre parenthèses */
                        <p className="text-xs font-semibold text-[#b45f2e] mt-0.5">
                          {formatQty(item.piece_count)} pièce{item.piece_count > 1 ? 's' : ''}
                          {item.weight_grams ? ` (~${formatQty(item.weight_grams)} g)` : ''}
                          {item.quantity > 1 ? ` × ${formatQty(item.quantity)}` : ''}
                        </p>
                      ) : item.weight_grams ? (
                        <p className="text-xs font-semibold text-[#b45f2e] mt-0.5">
                          {item.weight_grams >= 1000
                            ? `${formatQty(item.weight_grams / 1000)} kg`
                            : `${formatQty(item.weight_grams)} g`}
                          {item.quantity > 1 ? ` × ${formatQty(item.quantity)}` : ''}
                        </p>
                      ) : null}
                      <p className="text-xs text-gray-600 mt-0.5">
                        {formatPrice(item.unit_price_cents)} / {item.product.unit || 'piece'}
                      </p>

                      <div className="flex items-center justify-between mt-2">
                        {/* Quantity controls */}
                        {!isLocked ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                item.quantity <= 1
                                  ? removeItem(cleArticle(item))
                                  : updateQuantity(cleArticle(item), item.quantity - 1)
                              }
                              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
                            >
                              {item.quantity <= 1 ? (
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              ) : (
                                <Minus className="w-3.5 h-3.5 text-gray-600" />
                              )}
                            </button>
                            {/* « ×2 » plutôt que « 2 » quand une portion est
                                pesée : entre deux boutons, un nombre nu se lit
                                comme un poids. */}
                            <span className="w-8 text-center font-semibold text-sm">
                              {item.weight_grams ? `×${formatQty(item.quantity)}` : formatQty(item.quantity)}
                            </span>
                            <button
                              onClick={() => updateQuantity(cleArticle(item), item.quantity + 1)}
                              className="w-8 h-8 rounded-full flex items-center justify-center active:opacity-80"
                              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                            {/* Ligne pesée ou à la pièce : rechoisir le poids /
                                le nombre de pièces, sans repasser par la fiche */}
                            {item.weight_grams ? (
                              <button
                                onClick={() => setEditing(item)}
                                className="ml-1 h-8 px-2.5 rounded-full flex items-center gap-1 text-xs font-semibold active:opacity-80"
                                style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                                aria-label="Modifier le poids ou le nombre de pièces"
                              >
                                <Pencil className="w-3 h-3" />
                                Modifier
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                            Ajout vendeur
                          </span>
                        )}

                        {/* Total de la ligne + retrait d'un geste (avant, la
                            corbeille n'apparaissait qu'à la quantité 1) */}
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm" style={{ color: primaryColor }}>
                            {formatPrice(item.total_price_cents)}
                          </span>
                          {!isLocked && (
                            <button
                              onClick={() => removeItem(cleArticle(item))}
                              className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center active:bg-red-100"
                              aria-label="Retirer du panier"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vidanges rapportées au retrait : se décident en regardant son panier
              (Philippe, 04/10/2026 — au seul checkout, l'invite passait inaperçue). */}
          <div className="px-4 pb-4 space-y-3">
            <VidangesAnnonce value={vidanges} onChange={setVidanges} variante="panier" />
            <BacsAnnonce value={bacs} onChange={setBacs} variante="panier" />
          </div>

          {/* Footer with totals and checkout */}
          <div className="sticky bottom-16 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
            {/* Un consommateur ne paie pas le hors-taxes et n'a que faire de la
                ventilation : l'usage belge en vente aux particuliers est
                d'annoncer le prix payé. La TVA reste sur la facture. */}
            {/* La consigne se rend au magasin : le client doit la voir avant
                de payer, pas la découvrir sur le ticket. */}
            {totals.deposit_lines.map(l => (
              <div key={l.name} className="flex items-center justify-between mb-1 text-sm text-gray-600">
                <span>♻️ {l.name} × {l.quantity}</span>
                <span className="tabular-nums">{(l.total_cents / 100).toFixed(2)} &euro;</span>
              </div>
            ))}
            <div className="flex items-center justify-between mb-3 text-lg font-bold text-gray-900">
              <span>Total{totals.deposit_total_cents > 0 ? ' (consignes comprises)' : ''}</span>
              <span>{totals.total_ttc_euros} &euro;</span>
            </div>
            {/* Hors fenêtre, seul le FRAIS est bloqué : on ne barre le passage
                que si le panier en contient, et on dit lequel. */}
            {articlesBloques.length > 0 && (
              <p className="mb-2 text-sm text-center text-[#4a5c3a] font-medium">
                {articlesBloques.map(i => i.product.name).join(', ')} —
                {' '}commandable{articlesBloques.length > 1 ? 's' : ''}{' '}
                {formatJourReouverture(ordering?.next_open_at)}
                {ordering?.next_open_at ? ` (${formatMomentBelge(ordering.next_open_at)})` : ''}.
                {' '}Retirez {articlesBloques.length > 1 ? 'ces articles' : 'cet article'} pour commander le reste.
              </p>
            )}
            <button
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/shop/login', { state: { from: '/shop/checkout' } });
                } else {
                  navigate('/shop/checkout');
                }
              }}
              disabled={articlesBloques.length > 0}
              className="w-full py-3.5 rounded-xl text-white font-bold text-base active:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
            >
              {articlesBloques.length > 0
                ? 'Article non disponible aujourd’hui'
                : isAuthenticated
                ? 'Commander'
                : 'Se connecter pour commander'}
            </button>
          </div>
        </>
      )}

      {/* Rechoisir le poids / le nombre de pièces d'une ligne. Clé par produit :
          l'état initial de la modal repart de la sélection actuelle. */}
      {editing && (
        <WeightSelectionModal
          key={cleArticle(editing)}
          isOpen
          onClose={() => setEditing(null)}
          product={editing.product}
          confirmVerb="Modifier"
          initial={{
            weightGrams: editing.weight_grams || 0,
            quantityUnit: editing.quantity_unit || 'grams',
            pieceCount: editing.piece_count,
          }}
          onConfirm={(selection) => updateSelection(cleArticle(editing), selection)}
        />
      )}
    </div>
  );
}
